import _ from 'lodash';

import { useSelector, useI18n } from '~/Hooks';
import { useEntryFinder } from '~Components/EntryFinder';
import { useApolloClient } from '@apollo/client';
import { OrderDiscountOffer, NewDiscountType } from '@baemingo/offer';
import { CartDiscount } from '~Components/CartProvider';
import { DISCOUNT_TYPE } from '~../__queries__/globalTypes';
import { calculateOfferTotals, groupOfferMatches } from '~Utils/offers';
import useOffers from '~Hooks/useOffers';

export const useBasketCalculator = (
  venueId: string,
  appliedDiscount: CartDiscount | null = null,
) => {
  const findEntry = useEntryFinder();
  const client = useApolloClient();
  const menu = useSelector((state) => state.POSMenu);
  const { i18n } = useI18n();
  const { offers } = useOffers();

  const getSumFromItems = (
    items: { id: string; fraction?: number; price?: number; variationPrice?: number }[],
    _appliedDiscount?: CartDiscount | null,
  ) => {
    // If menu hasnt loaded yet
    if (!menu) {
      return [0, 0];
    }

    // Map old DISCOUNT_TYPE to new NewDiscountType
    const discountTypeMap: Record<DISCOUNT_TYPE, NewDiscountType> = {
      [DISCOUNT_TYPE.FIXED]: NewDiscountType.FIXED,
      [DISCOUNT_TYPE.PERCENTAGE]: NewDiscountType.PERCENTAGE,
      [DISCOUNT_TYPE.PRICE]: NewDiscountType.RELATIVE,
    };

    const rawOffer = _appliedDiscount ? offers.find((o) => o.id === _appliedDiscount.id) : null;

    let offer: OrderDiscountOffer | null = null;
    if (_appliedDiscount) {
      offer = new OrderDiscountOffer({
        discountType: discountTypeMap[_appliedDiscount.type],
        discountRate: _appliedDiscount.value,
        allowedCategoryIds: rawOffer ? rawOffer.categories.map((c) => c.id) : [],
        allowedItemIds: rawOffer ? rawOffer.items.map((i) => i.id) : [],
      });
    }

    const itemsExcludedByOffer: typeof items = [];
    const itemsIncludedInOffer: Array<{ id: string; itemId: string; amount: number }> = [];

    items.forEach(({ id, fraction, price, variationPrice }) => {
      const entry = menu.menuEntries.find((entry) => entry.item.id === id);
      let totalPrice = price ? price : entry ? entry.price : 0;
      if (fraction) {
        totalPrice = totalPrice * fraction;
      }
      // Add variation price, should not be fractional
      if (variationPrice) {
        totalPrice += variationPrice;
      }

      if (
        !_appliedDiscount ||
        (_appliedDiscount?.itemIds &&
          _appliedDiscount?.itemIds.length > 0 &&
          !_appliedDiscount.itemIds.includes(id))
      ) {
        itemsExcludedByOffer.push({ id, fraction, price: totalPrice, variationPrice });
      } else if (offer) {
        offer.addItem({
          id,
          itemId: id, // Use id as itemId for this case
          categoryId: '', // Not needed for discount calculation
          amount: totalPrice,
        });
        itemsIncludedInOffer.push({ id, itemId: id, amount: totalPrice });
      }
    });

    // Use the new calculate() API if we have an offer
    const changedItems = appliedDiscount && offer ? offer.calculate().changedItems : [];

    const _items = [
      // Set items we dont wish to discount to zero
      ...itemsExcludedByOffer.map((item) => ({
        id: item.id,
        price: item.price,
        discount: 0,
      })),
      // Set items we wish to discount to the correct discount
      ...changedItems.map((item: any) => ({
        id: item.id,
        price: item.amount, // Original price
        discount: item.discount, // Discount amount
      })),
    ];

    const sum = _.sumBy(_items, 'price') || 0;
    const discount = _.sumBy(_items, 'discount') || 0;

    return [sum, discount];
  };

  return (items: any[]) => {
    const groups = groupOfferMatches(items, venueId, client, i18n);

    const totals = calculateOfferTotals(
      // @ts-ignore
      groups.matches.map((match) => ({
        offerId: match.offerId,
        entries: match.items.map(({ id }) => {
          const item = items.find((i) => i.id === id);
          const entry = findEntry(item.itemId);
          return {
            ...entry,
            fraction: item.fraction,
          };
        }),
      })),
      venueId,
      client,
    );

    const offerVariationSum = groups.matches.reduce((acc, match) => {
      const varSum = _.sumBy(
        _.flatMap(match.items, (item) => item.variations),
        'price',
      );

      return acc + varSum;
    }, 0);

    // Get total amount of items that are included in offers
    let totalOfferAmount = _.sumBy(totals, 'price.amount');
    // The the discount these offers give
    let totalOfferDiscount = _.sumBy(totals, 'price.discount');

    // Retract fractional
    totals.forEach(({ price }) => {
      if (price.fraction) {
        const retractAmount = price.amount - price.amount * price.fraction;
        const retractDiscount = price.discount - price.discount * price.fraction;
        totalOfferAmount -= retractAmount;
        totalOfferDiscount -= retractDiscount;
      }
    });

    totalOfferAmount += offerVariationSum;

    // Get the sum of the items that are no part of bundle offers
    const [remainingSum, itemDiscount] = getSumFromItems(
      groups.unmatched.map((item) => ({
        id: item.itemId,
        fraction: item.fraction,
        price: item.price,
        variationPrice: _.sumBy(item.variations, 'price'),
      })),
      appliedDiscount,
    );

    // Combine the totals
    const total = remainingSum + totalOfferAmount;

    // Calculate applied discounts
    let discount = totalOfferDiscount + itemDiscount;

    if (discount > total) {
      discount = total;
    }

    return [total - discount, discount];
  };
};
